import { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { Form } from 'antd'
import Handlebars from 'handlebars'
import { CaseStatusEnum, PHYSICIAN_TAGS } from '../constants'
import decodeToken from '../utils/decodeToken'

export const useStatusUpdateModal = ({
    modalVisibility,
    onConfirm,
    actionCaseStatus,
    apiClasses,
    caseDetails, 
    activeRequest,
    provider,
}) => {
  const [form] = Form.useForm()
  const editorRef = useRef(null)

  const initialState = {
    selectedTemplateMessage: "",
    commonTemplateMessages: [],
    modalload: false,
    disableMessage: true,
    patientExplanation: null,
    requireInformationStatus: null,
    sendInformationCheckBox: actionCaseStatus !== CaseStatusEnum.REFER,
    templateFilter: "My",
    rejectionReason: null,
  };

  const [CaseUpdateStatusState, setCaseUpdateStatusState] = useState(initialState);

  const PROVIDER_NEEDS_REVIEW = useMemo(() => provider?.tags?.includes(PHYSICIAN_TAGS.NEEDS_REVIEW), [provider]);

  const requiresInformation = useMemo(() => [
    {
      _id: "5fc088f0dc6ef1ea454e2df2",
      name: "invalid face photo",
      displayName: CaseStatusEnum.INVALID_FACE_PHOTO,
    },
    {
      _id: "5fc08922dc6ef1ea454e2df3",
      name: "invalid photo id",
      displayName: CaseStatusEnum.INVALID_PHOTO_ID,
    },
    {
      _id: "5eeb23518a1eef7389dc1e84",
      name: "additional info required",
      displayName: CaseStatusEnum.ADDITIONAL_INFO_REQUIRED,
    },
  ], []);

  const user = useMemo(() => {
    const decodedToken = decodeToken(localStorage.getItem("token"));
    return decodedToken?.user;
  }, []);

  const sortFavorites = useCallback((templates) => {
    return [...templates].sort((a) => (a.favoritedBy ? -1 : 1));
  }, []);

  const compileHandlebars = useCallback((message) => {
    const handlebarsTemplate = Handlebars.compile(message);

    return handlebarsTemplate({
      case: caseDetails,
      physician: provider,
      patient: caseDetails?.patient,
    });
  }, [caseDetails, provider]);

  const getTemplateMessages = useCallback((status, category) => {
    const filters = {
      categoryIds: [category],
      status: status,
    };
    setCaseUpdateStatusState((prev) => ({ ...prev, modalload: true }));
    apiClasses.PhysicianAPI?.getTemplates("case", { filters })
      .then((response) => {
        setCaseUpdateStatusState((prev) => ({
          ...prev,
          commonTemplateMessages: sortFavorites(response.data.templates),
          modalload: false,
        }));
      })
      .catch((error) => {
        setCaseUpdateStatusState((prev) => ({ ...prev, modalload: false }));
      });
  }, [apiClasses, caseDetails?.categoryId]);

  const handlePatientExplanationChange = useCallback((value) => {
    setCaseUpdateStatusState((prev) => ({
      ...prev,
      patientExplanation: value,
    }));
    if (value !== CaseUpdateStatusState.selectedTemplateMessage) {
      setCaseUpdateStatusState((prev) => ({
        ...prev,
        selectedTemplateMessage: "",
      }));
    }
  }, [CaseUpdateStatusState.selectedTemplateMessage]);

  const handleTemplateSelectionChange = useCallback((value) => {
    const currentMessage = form.getFieldValue('patientExplanation') || ''
    const appendedMessage = currentMessage + `\n\n${value}`
    
    setCaseUpdateStatusState((prev) => ({
      ...prev,
      selectedTemplateMessage: value,
      patientExplanation: appendedMessage,
    }));

    form.setFieldsValue({ patientExplanation: appendedMessage });

    setTimeout(() => {
      const editor = editorRef.current?.editor
      if (editor) {
              const contentHeight = editor.getBody().scrollHeight;
              editor.getWin().scrollTo(0, contentHeight);
          }
  }, 0)
  }, [form]);

  const handleRejectionReasonChange = useCallback(({ target }) => {
    setCaseUpdateStatusState((prev) => ({
      ...prev,
      rejectionReason: target.value,
    }))
  }, []);

  const handleRequireInformationChange = useCallback((value) => {
    setCaseUpdateStatusState((prev) => ({
      ...prev,
      requireInformationStatus: value,
    }));
  }, []);

  const handleStatusUpdate = useCallback(() => {
    const finalStatus = actionCaseStatus === CaseStatusEnum.ADDITIONAL_INFO_REQUIRED ? CaseUpdateStatusState.requireInformationStatus : actionCaseStatus;

    let update = {
      status: finalStatus,
      patientExplanation: CaseUpdateStatusState.patientExplanation,
      sendToPatient: CaseUpdateStatusState.sendInformationCheckBox,
      rejectionReason: CaseUpdateStatusState.rejectionReason,
      requestId: activeRequest?._id,
    };

    if (finalStatus === CaseStatusEnum.REFER) {
      update.rejectionReason = CaseUpdateStatusState.rejectionReason;
    }

    onConfirm(update);
  }, [onConfirm, actionCaseStatus, CaseUpdateStatusState, activeRequest?._id]);

  useEffect(() => {
    setCaseUpdateStatusState((prev) => ({ ...prev, disableMessage: true }));
    if (
      modalVisibility &&
      actionCaseStatus !== CaseStatusEnum.ADDITIONAL_INFO_REQUIRED
    ) {
      getTemplateMessages(actionCaseStatus, caseDetails?.categoryId);
      setCaseUpdateStatusState((prev) => ({ ...prev, disableMessage: false }));
    }

    if (
      modalVisibility &&
      actionCaseStatus === CaseStatusEnum.ADDITIONAL_INFO_REQUIRED
    ) {
      setCaseUpdateStatusState((prev) => ({ ...prev, modalload: false }));
      if (CaseUpdateStatusState.requireInformationStatus) {
        getTemplateMessages(
          CaseUpdateStatusState.requireInformationStatus,
          caseDetails?.categoryId
        );
        setCaseUpdateStatusState((prev) => ({
          ...prev,
          disableMessage: false,
        }));
      }
    }
  }, [
    modalVisibility,
    actionCaseStatus,
    caseDetails?.categoryId,
    CaseUpdateStatusState.requireInformationStatus,
  ]);

  return {
    form, 
    CaseUpdateStatusState,
    PROVIDER_NEEDS_REVIEW,
    user,
    requiresInformation,
    setCaseUpdateStatusState,
    compileHandlebars,
    handlePatientExplanationChange,
    handleRequireInformationChange,
    handleTemplateSelectionChange,
    handleRejectionReasonChange,
    handleStatusUpdate,
    editorRef,
  }
}
